import { Box, Stack, Typography, useTheme } from "@mui/material";
import { ChevronRight } from "lucide-react";
import type { FC } from "react";
import type { TimelineItemResponseDTO } from "web_myplann/Infrastructure/Api/Api";
import { CategoryIcon } from "web_myplann/NotificationCenter/Components/CategoryIcon";
import { TimelineRowButton } from "web_myplann/NotificationCenter/Components/TimelineRowButton";

interface TimelineItemLayoutProps {
	item: TimelineItemResponseDTO;
	/** Right-hand date column, first line. */
	dateLine: string;
	/** Optional second line, for sections that need a date *and* a time. */
	secondaryDateLine?: string;
	unread?: boolean;
	showChevron?: boolean;
	disabled?: boolean;
	onClick?: () => void;
	isLast?: boolean;
	"data-testid"?: string;
}

const UNREAD_DOT_SIZE = 8;

const LINE_CLAMP_SX = {
	display: "-webkit-box",
	WebkitBoxOrient: "vertical",
	WebkitLineClamp: 1,
	overflow: "hidden",
} as const;

/**
 * Shared row layout for every notification timeline item. Behaviour that
 * genuinely differs per section (navigation, unread handling, how the date is
 * spelled out) stays with the caller — only the presentation lives here, so
 * the sections cannot drift apart visually again.
 */
const DateLine: FC<{ text: string }> = ({ text }) => {
	const theme = useTheme();

	return (
		<Typography fontSize={11} color={theme.palette.grey[500]} noWrap>
			{text}
		</Typography>
	);
};

export const TimelineItemLayout: FC<TimelineItemLayoutProps> = ({
	item,
	dateLine,
	secondaryDateLine,
	unread = false,
	showChevron = false,
	disabled = false,
	onClick,
	isLast = false,
	"data-testid": dataTestId,
}) => {
	const theme = useTheme();

	return (
		<TimelineRowButton
			isLast={isLast}
			backgroundColor={unread ? theme.palette.blue[50] : undefined}
			disabled={disabled}
			onClick={onClick}
			sx={{ position: "relative", py: 1.2, pl: 2.2, pr: 1.6 }}
			data-testid={dataTestId}
		>
			{unread && (
				<Box
					aria-hidden
					sx={{
						position: "absolute",
						left: 6,
						top: "50%",
						transform: "translateY(-50%)",
						width: UNREAD_DOT_SIZE,
						height: UNREAD_DOT_SIZE,
						borderRadius: "50%",
						backgroundColor: theme.palette.secondary.main,
					}}
				/>
			)}

			<Stack direction="row" gap={1.6} width="100%" alignItems="flex-start">
				<Box pt={0.4}>
					<CategoryIcon item={item} />
				</Box>

				<Stack flex={1} minWidth={0} gap={0.4}>
					<Typography
						fontSize={14}
						fontWeight={
							unread
								? theme.typography.fontWeightBold
								: theme.typography.fontWeightMedium
						}
						color={theme.palette.grey[900]}
						sx={LINE_CLAMP_SX}
					>
						{item.title}
					</Typography>
					{item.content ? (
						<Typography
							fontSize={12}
							color={theme.palette.grey[700]}
							sx={LINE_CLAMP_SX}
						>
							{item.content}
						</Typography>
					) : null}
					{item.subContent ? (
						<Typography
							fontSize={12}
							color={theme.palette.grey[500]}
							sx={LINE_CLAMP_SX}
						>
							{item.subContent}
						</Typography>
					) : null}
				</Stack>

				<Stack direction="row" alignItems="center" gap={0.6} flexShrink={0}>
					<Stack alignItems="flex-end">
						<DateLine text={dateLine} />
						{secondaryDateLine ? <DateLine text={secondaryDateLine} /> : null}
					</Stack>
					{showChevron ? (
						<ChevronRight size={20} color={theme.palette.grey[400]} />
					) : null}
				</Stack>
			</Stack>
		</TimelineRowButton>
	);
};
